import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Plus,
  ArrowLeft,
  Save,
  Trash2,
  Building2,
  LayoutGrid,
  Star,
  ChevronDown,
  RotateCcw,
  Users,
  UserPlus,
  Clock,
  X,
  AlertTriangle,
  Globe,
  Copy,
  Layers,
} from 'lucide-react';
import { confirm, alertDialog } from '../components/ui/confirm-dialog';
import { useToast } from '../components/ui/use-toast';
import { TimePicker } from '../components/ui/time-picker';
import { SearchableSelect } from '../components/ui/searchable-select';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import { locationsService } from '../services/locationsService';
import { geoService } from '../services/geoService';
import { restaurantGroupsService } from '../services/restaurantGroupsService';
import { staffService } from '../services/staffService';
import { useModuleAccess } from '../utils/useModuleAccess';
import AccessDenied from '../components/AccessDenied';
import FloorPlanDesigner from '../components/FloorPlanDesigner';

/* ------------------------------- small inputs ------------------------------ */

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring disabled:bg-muted disabled:text-muted-foreground';

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function TextInput(props) {
  return <input className={inputCls} {...props} value={props.value ?? ''} />;
}

// Mauritius phone input: a fixed, non-editable "+230" prefix and one field for
// the rest of the number. Stores the full "+230 <local>" string (or '' when the
// local part is blank) so downstream (booking, website) gets a dialable number.
function MuPhoneInput({ value, onChange, placeholder = '5 123 4567' }) {
  const local = String(value ?? '').replace(/^\s*\+?230\s*/, '');
  const emit = (raw) => {
    const l = String(raw).replace(/^\s*\+?230\s*/, ''); // guard against a pasted +230
    onChange(l.trim() ? `+230 ${l}` : '');
  };
  return (
    <div className="flex">
      <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground select-none">
        +230
      </span>
      <input
        type="tel"
        inputMode="tel"
        className={`${inputCls} rounded-l-none`}
        value={local}
        placeholder={placeholder}
        onChange={(e) => emit(e.target.value)}
      />
    </div>
  );
}

function TextArea({ rows = 3, ...rest }) {
  return <textarea rows={rows} className={`${inputCls} resize-y`} {...rest} value={rest.value ?? ''} />;
}

function SelectInput({ value, onChange, children, ...rest }) {
  return (
    <div className="relative">
      <select
        className={`${inputCls} appearance-none pr-9`}
        value={value ?? ''}
        onChange={onChange}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
      />
      {label}
    </label>
  );
}

/* --------------------------------- page ---------------------------------- */

// Operating hours are mandatory — new/edited locations start from a sensible
// open week (Mon–Sun, 09:00–17:00) so the form always has a value to save.
const makeDefaultHours = () =>
  Array.from({ length: 7 }, () => ({ closed: false, open: '09:00', close: '17:00' }));

/* Base URL of the PUBLIC marketing site (the TIS/Wrap & Roll web page), which is
   a SEPARATE site from this admin app. The admin lives at the domain root, so
   the marketing site is served alongside it under "/site" on the same tenant
   domain (nginx maps /site/ → the tis-website static files — see ops). That path
   is derived from the current origin, so it stays correct for every tenant
   without a per-tenant build. Override with REACT_APP_PUBLIC_SITE_URL if the
   site is hosted somewhere else (e.g. https://theindiansymphony.mu, or
   http://localhost:5500 in dev). Trailing slash trimmed so we never build "//". */
const publicSiteBase = () =>
  (process.env.REACT_APP_PUBLIC_SITE_URL || `${window.location.origin}/site`).replace(/\/+$/, '');

// One public website for the whole restaurant — the same address for every
// branch (no per-location slug). Ends with a trailing slash so it reads as a
// clean site root.
const publicSiteUrl = () => `${publicSiteBase()}/`;

const EMPTY_LOCATION = {
  name: '',
  phone: '',
  reservation_phone: '',
  alternate_phone: '',
  email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  // Town / village from the island gazetteer (apps/geo); the district follows from it.
  locality_id: null,
  description: '',
  public_site_slug: '',
  // Guest table finder (public site /near-me): where the branch is and how its card reads.
  latitude: '',
  longitude: '',
  area_label: '',
  cuisine_tag: '',
  is_public: true,
  is_primary: false,
  is_active: true,
  order_code: '',
  // Restaurant group / franchise this branch runs under - required: Company → Group → Location.
  group_id: null,
  operating_hours: makeDefaultHours(),
  last_order_time: '',
  // New branches default to "last orders 30 minutes before we close" — the
  // answer nearly every restaurant would give, and it follows each day's hours.
  last_order_offset_minutes: 30,
};

const TABS = [
  { key: 'basic', label: 'Basic Settings', icon: Building2 },
  { key: 'hours', label: 'Hours', icon: Clock },
  { key: 'admins', label: 'Admins', icon: Users },
  { key: 'seating', label: 'Floor Plan', icon: LayoutGrid },
];

// 0=Monday … 6=Sunday — matches Python's date.weekday() and the backend's
// Location.hours_for(weekday) helper.
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// "17:30" → "5:30 PM". Hours are stored and compared as 24h "HH:MM"; this is
// only for reading them back, since the rest of this screen speaks 12h.
function to12h(hhmm) {
  const [h, m] = String(hhmm || '').split(':');
  if (h === undefined || m === undefined) return '';
  const hour = Number(h);
  return `${hour % 12 || 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}

// Coerce stored operating_hours (or null) into a 7-row working array.
function normalizeHours(value) {
  return WEEKDAYS.map((_, i) => {
    const e = Array.isArray(value) ? value[i] : null;
    if (e && typeof e === 'object') {
      return { closed: !!e.closed, open: e.open || '', close: e.close || '' };
    }
    return { closed: false, open: '', close: '' };
  });
}

export default function LocationsManagement() {
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { canView, canEdit, canCreate } = useModuleAccess('locations');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Editor state — null = showing list; object = showing editor for that location
  // For "new", we use an object with id=null and EMPTY_LOCATION fields
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Restaurant groups (franchises / brands) of the company, for the form's picker and the list filter.
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('all');   // 'all' | 'none' | <group id as string>

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      locationsService.list(restaurantId, { includeInactive: showInactive }),
      // Inactive groups included so a branch still mapped to one keeps showing its name.
      restaurantGroupsService.list(restaurantId, { includeInactive: true }).then((r) => r.groups).catch(() => []),
    ])
      .then(([locs, gs]) => {
        if (cancelled) return;
        setLocations(locs);
        setGroups(gs);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message || 'Failed to load locations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantId, showInactive]);

  const reloadList = async () => {
    if (!restaurantId) return;
    const locs = await locationsService.list(restaurantId, { includeInactive: showInactive });
    setLocations(locs);
  };

  const reactivateLocation = async (loc) => {
    if (!restaurantId) return;
    try {
      await locationsService.update(restaurantId, loc.id, { is_active: true });
      await reloadList();
    } catch (e) {
      await alertDialog({ title: 'Error', description: `${e?.response?.data?.error || 'Failed to reactivate'}`, tone: 'destructive' });
    }
  };

  const openNew = () => {
    setEditing({ id: null, ...EMPTY_LOCATION });
    setActiveTab('basic');
    setSaveError(null);
  };

  const openExisting = (loc) => {
    setEditing({
      id: loc.id,
      name: loc.name || '',
      phone: loc.phone || '',
      reservation_phone: loc.reservation_phone || '',
      alternate_phone: loc.alternate_phone || '',
      email: loc.email || '',
      address_line1: loc.address_line1 || '',
      address_line2: loc.address_line2 || '',
      city: loc.city || '',
      state: loc.state || '',
      postal_code: loc.postal_code || '',
      country: loc.country || '',
      locality_id: loc.locality_id ?? null,
      description: loc.description || '',
      public_site_slug: loc.public_site_slug || '',
      latitude: loc.latitude ?? '',
      longitude: loc.longitude ?? '',
      area_label: loc.area_label || '',
      cuisine_tag: loc.cuisine_tag || '',
      is_public: loc.is_public !== false,
      is_primary: !!loc.is_primary,
      is_active: !!loc.is_active,
      order_code: loc.order_code || '',
      group_id: loc.group_id ?? null,
      operating_hours: Array.isArray(loc.operating_hours) && loc.operating_hours.length
        ? loc.operating_hours
        : makeDefaultHours(),
      last_order_time: loc.last_order_time || '',
      // null is meaningful here (= "use the fixed time"), so don't collapse it
      // to a default the way the blank strings above are treated.
      last_order_offset_minutes: loc.last_order_offset_minutes ?? null,
    });
    setActiveTab('basic');
    setSaveError(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setSaveError(null);
  };

  const setField = (field, value) => {
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    if (!restaurantId || !editing) return;
    if (!editing.name?.trim()) {
      setSaveError('Name is required');
      setActiveTab('basic');
      return;
    }
    // Every branch belongs to a restaurant group — Company → Group → Location.
    if (!editing.group_id) {
      setSaveError(t('settings.locations.validation.group'));
      setActiveTab('basic');
      return;
    }
    // Operating hours are mandatory: a valid 7-day set where every open day has
    // both an open and a close time.
    const hrs = editing.operating_hours;
    if (!Array.isArray(hrs) || hrs.length === 0 ||
        hrs.some((d) => !d?.closed && (!d?.open || !d?.close))) {
      setSaveError('Set operating hours for each day (open & close times, or mark the day closed).');
      setActiveTab('hours');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = { ...editing };
      delete payload.id;
      let saved;
      if (editing.id) {
        saved = await locationsService.update(restaurantId, editing.id, payload);
      } else {
        saved = await locationsService.create(restaurantId, payload);
      }
      await reloadList();
      setEditing(null);
      // Optional: re-open just-saved for further edits
      void saved;
    } catch (e) {
      setSaveError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const visibleLocations = useMemo(() => {
    if (groupFilter === 'all') return locations;
    if (groupFilter === 'none') return locations.filter((l) => !l.group_id);
    return locations.filter((l) => String(l.group_id) === groupFilter);
  }, [locations, groupFilter]);

  const removeLocation = async (loc) => {
    if (!restaurantId) return;
    if (!(await confirm({ description: `Deactivate location "${loc.name}"? It can be reactivated by support.`, confirmText: 'Deactivate', tone: 'destructive' }))) return;
    try {
      await locationsService.remove(restaurantId, loc.id);
      await reloadList();
    } catch (e) {
      await alertDialog({ title: 'Error', description: `${e?.response?.data?.error || 'Failed to deactivate'}`, tone: 'destructive' });
    }
  };

  if (!restaurantId) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          No restaurant selected. Pick a restaurant first to manage its locations.
        </div>
      </div>
    );
  }

  if (!canView) {
    return <AccessDenied perm="locations.view" what="the Locations page" />;
  }

  return (
    <div className="p-6 w-full max-w-full">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Locations</h1>
        </div>
        {!editing && (
          <div className="flex items-center gap-4 shrink-0">
            {groups.length > 0 && (
              <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Layers size={14} />
                <span className="sr-only">{t('settings.locations.groupFilter.label')}</span>
                <select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30"
                >
                  <option value="all">{t('settings.locations.groupFilter.all')}</option>
                  {locations.some((l) => !l.group_id) && (
                    <option value="none">{t('settings.locations.groupFilter.none')}</option>
                  )}
                  {groups.map((g) => (
                    <option key={g.id} value={String(g.id)}>{g.name}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              Show inactive
            </label>
            {canCreate && (
              <button
                type="button"
                onClick={openNew}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} /> Add Location
              </button>
            )}
          </div>
        )}
      </header>

      {!canEdit && (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
          <strong>Read-only access.</strong> Your role can view locations but cannot edit them.
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Which branch the public website speaks for is chosen in Settings →
          Website Settings → Site; this page only defines the branches. */}
      {loading && !editing ? (
        <div className="rounded-md border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
          Loading locations…
        </div>
      ) : editing ? (
        <LocationEditor
          editing={editing}
          groups={groups}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setField={setField}
          onClose={closeEditor}
          onSave={save}
          saving={saving}
          saveError={saveError}
          canEdit={canEdit}
          restaurantId={restaurantId}
        />
      ) : (
        <LocationList
          locations={visibleLocations}
          groups={groups}
          onOpen={openExisting}
          onRemove={removeLocation}
          onReactivate={reactivateLocation}
          onAdd={openNew}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

/* --------------------------------- list --------------------------------- */

function LocationList({ locations, groups = [], onOpen, onRemove, onReactivate, onAdd, canEdit = true }) {
  const { t } = useT();

  // Company → Restaurant Group → Location: the cards are arranged under their
  // group. A branch still in no group (legacy data) is listed last so it gets
  // noticed; a company with no groups at all keeps the flat grid.
  const sections = useMemo(() => {
    if (!groups.length) return [{ key: 'all', group: null, locations }];
    const byGroup = new Map();
    const ungrouped = [];
    locations.forEach((loc) => {
      if (!loc.group_id) { ungrouped.push(loc); return; }
      if (!byGroup.has(loc.group_id)) byGroup.set(loc.group_id, []);
      byGroup.get(loc.group_id).push(loc);
    });
    const out = groups
      .filter((g) => byGroup.has(g.id))
      .map((g) => ({ key: String(g.id), group: g, locations: byGroup.get(g.id) }));
    byGroup.forEach((locs, gid) => {
      if (!groups.some((g) => g.id === gid)) {
        out.push({ key: String(gid), group: locs[0].group || { id: gid, name: '' }, locations: locs });
      }
    });
    if (ungrouped.length) out.push({ key: 'none', group: null, locations: ungrouped });
    return out;
  }, [groups, locations]);

  if (!locations.length) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border bg-card px-6 py-12 text-center">
        <MapPin className="mx-auto h-10 w-10 text-muted-foreground" />
        <h3 className="mt-3 text-sm font-semibold text-foreground">No locations yet</h3>
        <p className="mt-1 text-sm text-muted-foreground">Get started by adding your first location.</p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} /> Add Location
        </button>
      </div>
    );
  }

  const renderCard = (loc) => (
        <div
          key={loc.id}
          className={`rounded-lg border p-4 transition-all ${
            loc.is_active
              ? 'border-border bg-card hover:border-primary/30 hover:shadow-sm'
              : 'border-dashed border-border bg-muted'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className={`text-base font-semibold truncate ${loc.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {loc.name}
                </h3>
                {loc.is_primary && loc.is_active && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-medium text-warning">
                    <Star size={10} /> Primary
                  </span>
                )}
                {!loc.is_active && (
                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    Inactive
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {loc.is_active ? (
                <>
                  <button
                    type="button"
                    onClick={() => onOpen(loc)}
                    className="text-xs font-medium text-primary hover:underline px-2 py-1"
                  >
                    {canEdit ? 'Edit' : 'View'}
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => onRemove(loc)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      title="Deactivate"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </>
              ) : (
                canEdit && (
                  <button
                    type="button"
                    onClick={() => onReactivate(loc)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground hover:bg-accent/70"
                    title="Reactivate"
                  >
                    <RotateCcw size={13} /> Reactivate
                  </button>
                )
              )}
            </div>
          </div>
          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
            {loc.phone && <div>📞 {loc.phone}</div>}
            {(loc.address_line1 || loc.city) && (
              <div className="truncate">
                {[loc.address_line1, loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
              </div>
            )}
            {/* Public marketing website — the same address for every branch. */}
            {publicSiteUrl() && (
              <div className="flex items-center gap-2 pt-1">
                <a
                  href={publicSiteUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-primary hover:underline truncate"
                  title={publicSiteUrl()}
                >
                  <Globe size={12} className="shrink-0" />
                  <span className="truncate">{publicSiteUrl().replace(/^https?:\/\//, '')}</span>
                </a>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(publicSiteUrl())}
                  className="text-muted-foreground hover:text-foreground p-0.5 shrink-0"
                  title="Copy link"
                >
                  <Copy size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
  );

  return (
    <div className="space-y-6">
      {sections.map((s) => (
        <section key={s.key}>
          {groups.length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <Layers size={14} className="text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">
                {s.group ? s.group.name : t('settings.locations.section.ungrouped')}
              </h2>
              {s.group?.code && (
                <span className="inline-flex items-center rounded bg-accent px-1.5 py-0.5 text-[10px] font-mono font-medium text-accent-foreground">
                  {s.group.code}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {t('settings.locations.section.count', { count: s.locations.length })}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.locations.map(renderCard)}
          </div>
        </section>
      ))}
    </div>
  );
}

/* --------------------------------- editor --------------------------------- */

function LocationEditor({
  editing, groups = [], activeTab, setActiveTab, setField,
  onClose, onSave, saving, saveError, canEdit = true, restaurantId,
}) {
  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="border-b border-border px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onClose}
            disabled={false}
            className="text-muted-foreground hover:text-foreground p-1 -ml-1"
            title="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-base font-semibold text-foreground truncate">
            {editing.id ? `${canEdit ? 'Edit' : 'View'}: ${editing.name || 'Location'}` : 'New Location'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-md"
          >
            {canEdit ? 'Cancel' : 'Close'}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-5 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors ${
                isActive
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon size={14} /> {t.label}
              {t.soon && (
                <span className="ml-1 text-[9px] uppercase tracking-wider rounded px-1 py-0.5 bg-muted text-muted-foreground">
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {activeTab === 'seating' ? (
        editing.id ? (
          <div className="h-[calc(100vh-260px)] min-h-[480px]">
            <FloorPlanDesigner
              restaurantId={restaurantId}
              locationId={editing.id}
              locationName={editing.name}
            />
          </div>
        ) : (
          <div className="p-5">
            <SaveLocationFirstPlaceholder what="admins and floor plan" />
          </div>
        )
      ) : activeTab === 'admins' ? (
        <div className="p-5">
          {editing.id ? (
            <AdminsTab restaurantId={restaurantId} locationId={editing.id} canEdit={canEdit} />
          ) : (
            <SaveLocationFirstPlaceholder what="admins" />
          )}
        </div>
      ) : (
        <div className="p-5">
          {saveError && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {saveError}
            </div>
          )}

          {activeTab === 'basic' && <BasicTab editing={editing} setField={setField} groups={groups} />}
          {activeTab === 'hours' && <HoursTab editing={editing} setField={setField} />}
        </div>
      )}
    </div>
    </fieldset>
  );
}

/* ------------------------------- Basic tab ------------------------------- */

function BasicTab({ editing, setField, groups = [] }) {
  const { t } = useT();
  // Active groups, plus the one this branch already sits in even if it was deactivated since.
  const pickableGroups = groups.filter((g) => g.is_active !== false || g.id === editing.group_id);
  // Island gazetteer for the Locality picker: every active town / village /
  // quarter with its district, fetched once and filtered as the user types.
  const [localities, setLocalities] = useState([]);
  const [localitiesLoading, setLocalitiesLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    geoService
      .localitiesAll()
      .then((rows) => { if (alive) setLocalities(rows); })
      .catch(() => {})
      .finally(() => { if (alive) setLocalitiesLoading(false); });
    return () => { alive = false; };
  }, []);
  const localityOptions = useMemo(
    () => [
      { value: '', label: t('settings.locations.locality.none') },
      ...localities.map((l) => ({ value: l.id, label: l.name, description: l.district?.name || '' })),
    ],
    [localities, t],
  );
  const pickedLocality = localities.find((l) => l.id === editing.locality_id) || null;
  const pickLocality = (v) => {
    const id = v === '' || v == null ? null : Number(v);
    setField('locality_id', id);
    // The mapping does the typing: a blank City / District takes the locality's own words.
    const row = localities.find((l) => l.id === id);
    if (row) {
      if (!String(editing.city || '').trim()) setField('city', row.name);
      if (!String(editing.state || '').trim()) setField('state', row.district?.name || '');
    }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Location Name" required>
        <TextInput
          value={editing.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="e.g., Downtown Branch"
        />
      </Field>
      <Field label={t('settings.locations.field.group')} required hint={t('settings.locations.field.groupHint')}>
        <SelectInput
          value={editing.group_id ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            setField('group_id', v ? Number(v) : null);
          }}
        >
          <option value="" disabled={pickableGroups.length > 0}>{t('settings.locations.field.groupSelect')}</option>
          {pickableGroups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}{g.code ? ` (${g.code})` : ''}</option>
          ))}
        </SelectInput>
        {!pickableGroups.length && (
          <span className="mt-1 flex items-start gap-1.5 text-[11px] text-warning">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>
              {t('settings.locations.field.groupNoneYet')}{' '}
              <Link to="/settings/restaurant-groups" className="font-medium underline">
                {t('settings.locations.field.groupNoneYetLink')}
              </Link>.
            </span>
          </span>
        )}
      </Field>
      <Field label="Order Code">
        <TextInput
          value={editing.order_code}
          onChange={(e) => setField('order_code', e.target.value.toUpperCase().slice(0, 3))}
          placeholder="001"
          maxLength={3}
        />
      </Field>
      <Field label="Phone">
        <MuPhoneInput value={editing.phone} onChange={(v) => setField('phone', v)} />
      </Field>

      <Field label="Table Reservation Number">
        <MuPhoneInput value={editing.reservation_phone} onChange={(v) => setField('reservation_phone', v)} />
      </Field>

      <Field label="Alternate Contact Number">
        <MuPhoneInput value={editing.alternate_phone} onChange={(v) => setField('alternate_phone', v)} />
      </Field>

      <Field label="Email">
        <TextInput
          type="email"
          value={editing.email}
          onChange={(e) => setField('email', e.target.value)}
          placeholder="branch@example.com"
        />
      </Field>
      <Field label="Postal Code">
        <TextInput
          value={editing.postal_code}
          onChange={(e) => setField('postal_code', e.target.value)}
        />
      </Field>

      <div className="md:col-span-2">
        <Field label="Address Line 1">
          <TextInput
            value={editing.address_line1}
            onChange={(e) => setField('address_line1', e.target.value)}
            placeholder="Street, building"
          />
        </Field>
      </div>
      <div className="md:col-span-2">
        <Field label="Address Line 2">
          <TextInput
            value={editing.address_line2}
            onChange={(e) => setField('address_line2', e.target.value)}
            placeholder="Suite, floor (optional)"
          />
        </Field>
      </div>

      <div className="md:col-span-2">
        <Field
          label={t('settings.locations.locality.label')}
          hint={pickedLocality
            ? t('settings.locations.locality.district', { district: pickedLocality.district?.name || '' })
            : t('settings.locations.locality.hint')}
        >
          <SearchableSelect
            options={localityOptions}
            value={editing.locality_id ?? null}
            loading={localitiesLoading}
            placeholder={t('settings.locations.locality.placeholder')}
            searchPlaceholder={t('settings.locations.locality.search')}
            emptyText={t('settings.locations.locality.empty')}
            onChange={pickLocality}
          />
        </Field>
      </div>

      <Field label="City">
        <TextInput
          value={editing.city}
          onChange={(e) => setField('city', e.target.value)}
        />
      </Field>
      <Field label="State / District">
        <TextInput
          value={editing.state}
          onChange={(e) => setField('state', e.target.value)}
        />
      </Field>

      <Field label="Country">
        <TextInput
          value={editing.country}
          onChange={(e) => setField('country', e.target.value)}
          placeholder="Mauritius"
        />
      </Field>
      {/* Guest table finder (public site /near-me): pin the branch and say what it is. */}
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-4">
        <div className="md:col-span-2 text-sm font-medium text-foreground">{t('settings.locations.finder.title')}</div>
        <Field label={t('settings.locations.finder.latitude')} hint={t('settings.locations.finder.latitudeHint')}>
          <TextInput
            type="number"
            step="0.000001"
            min="-90"
            max="90"
            inputMode="decimal"
            value={editing.latitude}
            onChange={(e) => setField('latitude', e.target.value)}
            placeholder="-20.161000"
          />
        </Field>
        <Field label={t('settings.locations.finder.longitude')} hint={t('settings.locations.finder.longitudeHint')}>
          <TextInput
            type="number"
            step="0.000001"
            min="-180"
            max="180"
            inputMode="decimal"
            value={editing.longitude}
            onChange={(e) => setField('longitude', e.target.value)}
            placeholder="57.499000"
          />
        </Field>
        <Field label={t('settings.locations.finder.areaLabel')} hint={t('settings.locations.finder.areaLabelHint')}>
          <TextInput
            value={editing.area_label}
            maxLength={120}
            onChange={(e) => setField('area_label', e.target.value)}
            placeholder={t('settings.locations.finder.areaLabelPh')}
          />
        </Field>
        <Field label={t('settings.locations.finder.cuisineTag')} hint={t('settings.locations.finder.cuisineTagHint')}>
          <TextInput
            value={editing.cuisine_tag}
            maxLength={80}
            onChange={(e) => setField('cuisine_tag', e.target.value)}
            placeholder={t('settings.locations.finder.cuisineTagPh')}
          />
        </Field>
        <div className="md:col-span-2">
          <Checkbox
            checked={editing.is_public}
            onChange={(e) => setField('is_public', e.target.checked)}
            label={t('settings.locations.finder.isPublic')}
          />
        </div>
      </div>
      <div className="flex items-end">
        <Checkbox
          checked={editing.is_primary}
          onChange={(e) => setField('is_primary', e.target.checked)}
          label="Mark as primary location"
        />
      </div>

      <div className="md:col-span-2">
        <Field label="Description / Notes">
          <TextArea
            value={editing.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder="Internal notes about this location…"
          />
        </Field>
      </div>
    </div>
  );
}

/* -------------------------------- Hours tab ------------------------------- */

function HoursTab({ editing, setField }) {
  const { toast } = useToast();
  const configured = Array.isArray(editing.operating_hours);
  const rows = normalizeHours(editing.operating_hours);

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setField('operating_hours', next);
  };

  // Copy Monday's window onto every other day — the common "same hours daily" case.
  const applyToAll = () => {
    const monday = rows[0];
    if (!monday.closed && (!monday.open || !monday.close)) {
      toast({
        title: 'Set Monday first',
        description: "Give Monday an open and close time (or mark it closed), then apply it to every day.",
        variant: 'destructive',
      });
      return;
    }
    setField('operating_hours', rows.map(() => ({ ...monday })));
    toast({
      title: 'Hours applied',
      description: monday.closed
        ? 'Every day is now marked closed (copied from Monday).'
        : `Every day is now ${monday.open}–${monday.close} (copied from Monday).`,
    });
  };

  // Hours are mandatory, so "reset" returns to the default open week rather
  // than clearing to nothing.
  const clearAll = () => setField('operating_hours', makeDefaultHours());

  // The kitchen can't take an order after the branch has shut. Both values are
  // canonical 24h "HH:MM", so a plain string compare orders them correctly.
  // A warning rather than a block: overnight windows (open 18:00, close 02:00)
  // are legitimate and would trip a naive rule. Only the fixed-time mode can
  // get this wrong — an offset is measured back from closing by definition.
  const lastOrder = editing.last_order_time || '';
  const offset = editing.last_order_offset_minutes;
  const usesOffset = offset !== null && offset !== undefined && offset !== '';
  const lateDays = (usesOffset || !lastOrder) ? [] : rows
    .map((r, i) => (!r.closed && r.close && r.close > r.open && lastOrder > r.close ? WEEKDAYS[i] : null))
    .filter(Boolean);

  // What the offset comes to on each day, computed here rather than waiting for
  // a save + reload — staff are choosing the number and want to see its effect.
  const offsetTimes = !usesOffset ? [] : rows.map((r) => {
    if (r.closed || !r.close) return null;
    const [hh, mm] = String(r.close).split(':');
    const at = (((+hh * 60 + +mm) - Number(offset)) % 1440 + 1440) % 1440;
    return `${String(Math.floor(at / 60)).padStart(2, '0')}:${String(at % 60).padStart(2, '0')}`;
  });

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Operating Hours</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={applyToAll}
            className="text-xs font-medium text-primary hover:underline"
          >
            Apply Monday’s hours to every day
          </button>
          {configured && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-muted-foreground hover:text-primary"
            >
              Reset hours
            </button>
          )}
        </div>
      </div>

      {!configured && (
        <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
          Hours not set yet. Edit any day below to start — sections set to “follow working hours” will use these.
        </div>
      )}

      <div className="space-y-2">
        {WEEKDAYS.map((day, idx) => {
          const row = rows[idx];
          return (
            <div key={day} className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3 py-2">
              <div className="w-24 text-sm font-medium text-foreground">{day}</div>
              <Checkbox
                checked={row.closed}
                onChange={(e) => update(idx, { closed: e.target.checked })}
                label="Closed"
              />
              {row.closed ? (
                <div className="text-xs italic text-muted-foreground flex-1">Closed all day</div>
              ) : (
                <div className="flex items-center gap-2 flex-1">
                  <TimePicker value={row.open} onChange={(v) => update(idx, { open: v })} />
                  <span className="text-xs text-muted-foreground">to</span>
                  <TimePicker value={row.close} onChange={(v) => update(idx, { close: v })} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Last order time — kitchen cut-off, shown to guests on the menu */}
      <div className="rounded-md border border-border px-3 py-3">
        <h3 className="text-sm font-semibold text-foreground">Last order time</h3>

        {/* Two ways to say it. The offset is the normal one — it follows each
            day's own closing time, so a short Sunday cuts off earlier without
            anyone maintaining seven times by hand. */}
        <div className="flex flex-wrap gap-4 mb-3">
          {[
            { key: 'offset', label: 'Before closing time' },
            { key: 'fixed', label: 'Same time every day' },
          ].map((m) => (
            <label key={m.key} className="inline-flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="radio"
                name="lastOrderMode"
                checked={m.key === 'offset' ? usesOffset : !usesOffset}
                onChange={() => {
                  if (m.key === 'offset') {
                    setField('last_order_offset_minutes', 30);
                    setField('last_order_time', '');
                  } else {
                    setField('last_order_offset_minutes', null);
                  }
                }}
                className="h-4 w-4 border-border text-primary focus:ring-ring"
              />
              {m.label}
            </label>
          ))}
        </div>

        {usesOffset ? (
          <>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <span>Last orders</span>
              <input
                type="number"
                min="0"
                max="720"
                step="5"
                value={offset}
                onChange={(e) => {
                  const v = e.target.value;
                  setField('last_order_offset_minutes', v === '' ? 0 : Math.max(0, Math.min(720, Number(v))));
                }}
                className="w-20 rounded-md border border-border bg-background px-2 py-1.5 text-sm
                           focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span>minutes before closing</span>
            </div>
            {/* The point of the offset is that each day differs — so show what
                it actually comes to, rather than making staff work it out. */}
            <div className="mt-3 grid gap-1 sm:grid-cols-2">
              {WEEKDAYS.map((day, i) => (
                <div key={day} className="flex items-center justify-between gap-3 rounded-md bg-muted px-2.5 py-1.5 text-xs">
                  <span className="text-muted-foreground">{day}</span>
                  {rows[i].closed ? (
                    <span className="italic text-muted-foreground">Closed</span>
                  ) : offsetTimes[i] ? (
                    <span className="font-medium text-foreground tabular-nums">
                      Last order {to12h(offsetTimes[i])}
                      <span className="text-muted-foreground font-normal">
                        {' '}· closes {to12h(rows[i].close)}
                      </span>
                    </span>
                  ) : (
                    <span className="italic text-muted-foreground">Set a closing time</span>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <TimePicker
              value={editing.last_order_time || ''}
              onChange={(v) => setField('last_order_time', v)}
            />
            {editing.last_order_time && (
              <button
                type="button"
                onClick={() => setField('last_order_time', '')}
                className="text-xs text-muted-foreground hover:text-primary"
              >
                Clear
              </button>
            )}
          </div>
        )}
        {lateDays.length > 0 && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              This is <strong>after closing</strong> on {lateDays.join(', ')} — set it a little
              before the earliest close, or clear it.
            </span>
          </div>
        )}
      </div>

    </div>
  );
}

/* ------------------------- Floor-plan placeholder ------------------------ */

function SaveLocationFirstPlaceholder({ what = 'the floor plan' }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-border bg-muted px-6 py-12 text-center">
      <LayoutGrid className="mx-auto h-10 w-10 text-muted-foreground" />
      <h3 className="mt-3 text-base font-semibold text-foreground">Save this location first</h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
        Fill in the basic info and click Save. Once the location exists, you can manage {what} here.
      </p>
    </div>
  );
}

/* ------------------------------- Admins tab ------------------------------ */

function AdminsTab({ restaurantId, locationId, canEdit = true }) {
  const [admins, setAdmins] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [adm, stf] = await Promise.all([
        locationsService.listAdmins(restaurantId, locationId),
        staffService.list(restaurantId),
      ]);
      setAdmins(adm);
      setStaff(stf);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId && locationId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, locationId]);

  // Staff not already mapped as admins — the picker's candidates.
  const assignedIds = new Set(admins.map((a) => a.staff_id));
  const candidates = staff.filter((s) => !assignedIds.has(s.id) && s.is_active !== false);

  const addAdmin = async () => {
    if (!selectedStaffId) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await locationsService.addAdmin(restaurantId, locationId, Number(selectedStaffId));
      setAdmins(Array.isArray(updated) ? updated : admins);
      setSelectedStaffId('');
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to add admin');
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (staffId) => {
    setBusy(true);
    setError(null);
    try {
      const res = await locationsService.removeAdmin(restaurantId, locationId, staffId);
      // Backend returns the refreshed admin list in `data` on success.
      if (res?.data) setAdmins(res.data);
      else setAdmins((prev) => prev.filter((a) => a.staff_id !== staffId));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to remove admin');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-6 text-center">Loading admins…</div>;
  }

  const lastOne = admins.length <= 1;

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Location Admins</h3>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Current admins */}
      {admins.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted px-3 py-4 text-sm text-muted-foreground text-center">
          No admins assigned yet. Add one below.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {admins.map((a) => (
            <li key={a.staff_id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {a.name || `Staff #${a.staff_id}`}
                </div>
                {a.email && <div className="text-xs text-muted-foreground truncate">{a.email}</div>}
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeAdmin(a.staff_id)}
                  disabled={busy || lastOne}
                  title={lastOne ? 'A location must keep at least one admin' : 'Remove admin'}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-40 disabled:hover:text-muted-foreground p-1"
                >
                  <X size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add admin */}
      {canEdit && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Add an admin</label>
            <SelectInput
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              disabled={busy || candidates.length === 0}
            >
              <option value="">
                {candidates.length === 0 ? '— No more staff to add —' : '— Select staff —'}
              </option>
              {candidates.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.full_name || `${s.first_name} ${s.last_name}`}{s.staff_type ? ` · ${s.staff_type}` : ''}
                </option>
              ))}
            </SelectInput>
          </div>
          <button
            type="button"
            onClick={addAdmin}
            disabled={busy || !selectedStaffId}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <UserPlus size={14} /> Add
          </button>
        </div>
      )}
    </div>
  );
}
